import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AdtechInfoDialog from './AdtechInfoDialog';
import AdtechDeviceActions from './AdtechDeviceActions';

/** One raw commercial mobile-location observation. Many rows can share a
 *  registration_id (the same device seen at multiple times/places). Field set
 *  mirrors Tracer's GeoNeutron area-search table. */
export interface AdtechMapSignal {
  registration_id: string;
  latitude: number;
  longitude: number;
  timestamp?: string | null;
  carrier?: string | null;
  country?: string | null;
  accuracy?: number | null;
  ip_address?: string | null;
  timezone?: string | null;
  forensic_flag?: string | null;
}

interface AdtechResultsTableProps {
  /** Signals loaded onto the map from an Area Search. */
  signals: AdtechMapSignal[];
  /** Hide the floating table only — map points and toolbar reopen stay. */
  onClose: () => void;
  /** Remove loaded AdTech signals from the map (also closes the table). */
  onClear: () => void;
  /** Fly the map to a signal + focus its device's points. */
  onRowClick?: (signal: AdtechMapSignal) => void;
  /** Currently focused registration id (row + map point highlight). */
  focusedId?: string | null;
  /** Human label for the source area/query (shown in the header). */
  sourceLabel?: string;
  /** Load full pattern-of-life for the given devices (single row or "all"). */
  onLoadPol?: (registrationIds: string[], label?: string) => void;
  /** True while a PoL request is in flight (disables buttons + shows a bar). */
  polLoading?: boolean;
  /** Status text shown while a PoL request is running. */
  polMessage?: string;
  /** Optional course scope for Saved Devices / watchlist. */
  courseId?: string;
}
// (props above drive the Tracer-style pattern-of-life + pagination features)

type SortKey = 'registration_id' | 'timestamp' | 'accuracy' | 'country' | 'carrier' | 'forensic_flag';

interface ColumnDef {
  key: string;
  label: string;
  align: 'left' | 'right';
  sortable: boolean;
  width?: number;
}

// Column set + order mirrors Tracer's area-search table (visible fields).
const COLUMNS: ColumnDef[] = [
  { key: 'registration_id', label: 'Registration ID', align: 'left', sortable: true, width: 230 },
  { key: 'timestamp', label: 'Time', align: 'left', sortable: true, width: 150 },
  { key: 'latitude', label: 'Lat', align: 'right', sortable: false, width: 90 },
  { key: 'longitude', label: 'Lon', align: 'right', sortable: false, width: 90 },
  { key: 'accuracy', label: 'Acc (m)', align: 'right', sortable: true, width: 70 },
  { key: 'ip_address', label: 'IP', align: 'left', sortable: false, width: 120 },
  { key: 'country', label: 'Country', align: 'left', sortable: true, width: 80 },
  { key: 'carrier', label: 'Carrier', align: 'left', sortable: true, width: 150 },
  { key: 'timezone', label: 'Timezone', align: 'left', sortable: false, width: 140 },
  { key: 'forensic_flag', label: 'Forensic', align: 'left', sortable: true, width: 150 },
];

const HEADER_HEIGHT = 40;
const MIN_WIDTH = 480;
const MIN_HEIGHT = 220;
const DEFAULT_WIDTH = 980;
const DEFAULT_HEIGHT = 380;
const MARGIN = 12;
const ROWS_PER_PAGE_OPTIONS = [50, 100, 250, 500];

function fmtTime(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Floating, non-modal, draggable/resizable results panel (Tracer-style table).
 *
 * Visibility is toggled from the map toolbar (does not clear map points).
 * Clear (X) removes both the panel and the pink AdTech layer. Rendered in a
 * portal with `position: fixed` so it is never clipped by the map area.
 */
const AdtechResultsTable: React.FC<AdtechResultsTableProps> = ({
  signals,
  onClose,
  onClear,
  onRowClick,
  focusedId,
  sourceLabel,
  onLoadPol,
  polLoading,
  polMessage,
  courseId,
}) => {
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [infoOpen, setInfoOpen] = useState(false);

  // Fixed (viewport) geometry, top/left anchored. Default: lower-left.
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [pos, setPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const h = typeof window !== 'undefined' ? window.innerHeight : 800;
    const width = Math.min(DEFAULT_WIDTH, w - 2 * MARGIN);
    return { left: MARGIN, top: Math.max(MARGIN, h - DEFAULT_HEIGHT - 96), width };
  });

  const dragRef = useRef<{
    mode: 'move' | 'resize-bottom' | 'resize-right' | 'resize-corner';
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const clampIntoView = useCallback((left: number, top: number, width: number, height: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(width, vw - 2 * MARGIN);
    return {
      left: clamp(left, MARGIN, Math.max(MARGIN, vw - w - MARGIN)),
      top: clamp(top, MARGIN, Math.max(MARGIN, vh - height - MARGIN)),
    };
  }, []);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.mode === 'move') {
      const next = clampIntoView(d.startLeft + dx, d.startTop + dy, d.startWidth, d.startHeight);
      setPos((p) => ({ ...p, left: next.left, top: next.top }));
    } else {
      setSize((s) => {
        let width = s.width;
        let height = s.height;
        if (d.mode === 'resize-right' || d.mode === 'resize-corner') {
          width = clamp(d.startWidth + dx, MIN_WIDTH, window.innerWidth - d.startLeft - MARGIN);
        }
        if (d.mode === 'resize-bottom' || d.mode === 'resize-corner') {
          height = clamp(d.startHeight + dy, MIN_HEIGHT, window.innerHeight - d.startTop - MARGIN);
        }
        return { width, height };
      });
    }
  }, [clampIntoView]);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    document.body.style.userSelect = '';
  }, [onPointerMove]);

  const beginDrag = useCallback(
    (mode: 'move' | 'resize-bottom' | 'resize-right' | 'resize-corner') => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {
        mode,
        startX: e.clientX,
        startY: e.clientY,
        startLeft: pos.left,
        startTop: pos.top,
        startWidth: size.width,
        startHeight: size.height,
      };
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
    },
    [pos, size, onPointerMove, endDrag],
  );

  useEffect(() => endDrag, [endDrag]);

  useEffect(() => {
    const reclamp = () => setPos((p) => ({ ...p, ...clampIntoView(p.left, p.top, size.width, size.height) }));
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [clampIntoView, size.width, size.height]);

  const uniqueDeviceIds = useMemo(
    () => Array.from(new Set(signals.map((s) => s.registration_id).filter(Boolean))),
    [signals],
  );

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = signals;
    if (q) {
      list = signals.filter(
        (s) =>
          (s.registration_id || '').toLowerCase().includes(q) ||
          (s.carrier || '').toLowerCase().includes(q) ||
          (s.country || '').toLowerCase().includes(q) ||
          (s.forensic_flag || '').toLowerCase().includes(q) ||
          (s.ip_address || '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [signals, filter, sortKey, sortDir]);

  // Reset to first page whenever the dataset / filter / sort changes.
  useEffect(() => { setPage(0); }, [filter, sortKey, sortDir, signals]);

  const pageCount = rows.length;
  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage],
  );

  const toggleSort = (key: string, sortable: boolean) => {
    if (!sortable) return;
    const k = key as SortKey;
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'timestamp' ? 'desc' : 'asc');
    }
  };

  const exportCsv = () => {
    const header = COLUMNS.map((c) => c.key);
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(',')];
    for (const s of rows) {
      lines.push(header.map((k) => escape((s as any)[k])).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adtech-signals-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (s: AdtechMapSignal, col: ColumnDef): React.ReactNode => {
    switch (col.key) {
      case 'registration_id':
        return <span style={{ fontFamily: 'monospace' }}>{s.registration_id}</span>;
      case 'timestamp':
        return fmtTime(s.timestamp);
      case 'latitude':
        return <span style={{ fontFamily: 'monospace' }}>{Number(s.latitude).toFixed(5)}</span>;
      case 'longitude':
        return <span style={{ fontFamily: 'monospace' }}>{Number(s.longitude).toFixed(5)}</span>;
      case 'accuracy':
        return s.accuracy != null ? Math.round(Number(s.accuracy)) : '—';
      case 'ip_address':
        return s.ip_address || '—';
      case 'country':
        return s.country || '—';
      case 'carrier':
        return s.carrier || '—';
      case 'timezone':
        return s.timezone || '—';
      case 'forensic_flag':
        return s.forensic_flag ? (
          <Chip size="small" label={s.forensic_flag} sx={{ height: 18, fontSize: '0.6rem' }} />
        ) : '—';
      default:
        return (s as any)[col.key] ?? '—';
    }
  };

  const sortArrow = (key: string) => (key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const headerCellSx = (col: ColumnDef) => ({
    px: 1,
    py: 0.5,
    fontWeight: 700,
    fontSize: '0.7rem',
    cursor: col.sortable ? 'pointer' : 'default',
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
    color: 'text.secondary',
    textAlign: col.align,
    minWidth: col.width,
    '&:hover': col.sortable ? { color: 'text.primary' } : {},
  });

  const panel = (
    <Paper
      elevation={12}
      sx={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        width: Math.min(size.width, (typeof window !== 'undefined' ? window.innerWidth : size.width) - 2 * MARGIN),
        height: size.height,
        zIndex: 1250,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(6px)',
      }}
    >
      {/* Header bar (drag handle). Show/hide is controlled by the map toolbar. */}
      <Box
        onPointerDown={beginDrag('move')}
        sx={{
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'move',
          background: 'linear-gradient(90deg, rgba(233,30,99,0.16), rgba(233,30,99,0.04))',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        <SignalCellularAltIcon sx={{ fontSize: 18, color: '#e91e63' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
          AdTech Results
        </Typography>
        <Chip
          size="small"
          label={`${signals.length} signals`}
          sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(233,30,99,0.15)', color: '#e91e63', fontWeight: 700 }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${uniqueDeviceIds.length} devices`}
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
        {sourceLabel && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
            {sourceLabel}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {onLoadPol && uniqueDeviceIds.length > 0 && (
          <Tooltip title="Load full pattern of life for every device below (adds them to the device tree)">
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<TimelineIcon sx={{ fontSize: 16 }} />}
                disabled={!!polLoading}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onLoadPol(uniqueDeviceIds, `AdTech PoL (${uniqueDeviceIds.length} devices)`); }}
                sx={{ mr: 0.5, fontSize: '0.68rem', bgcolor: '#e91e63', '&:hover': { bgcolor: '#c2185b' }, textTransform: 'none' }}
              >
                Load all PoL
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="AdTech forensic flags & quality reference">
          <IconButton
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setInfoOpen(true); }}
            sx={{ color: '#e91e63' }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export CSV">
          <span>
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); exportCsv(); }}
              disabled={rows.length === 0}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Clear AdTech results from map">
          <IconButton
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
          >
            <DeleteSweepIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Hide table (keeps map points — reopen from toolbar)">
          <IconButton
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* PoL progress bar */}
      {polLoading && (
        <Box sx={{ px: 0 }}>
          <LinearProgress sx={{ height: 3, '& .MuiLinearProgress-bar': { bgcolor: '#e91e63' }, bgcolor: 'rgba(233,30,99,0.15)' }} />
          {polMessage && (
            <Typography variant="caption" sx={{ px: 1, py: 0.25, display: 'block', color: '#e91e63' }}>
              {polMessage}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Filter row */}
          <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              placeholder="Filter by reg ID, carrier, country, IP, forensic flag…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.75rem' },
              }}
            />
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
              <Box
                component="thead"
                sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
              >
                <Box component="tr" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  {COLUMNS.map((col) => (
                    <Box
                      component="th"
                      key={col.key}
                      sx={headerCellSx(col)}
                      onClick={() => toggleSort(col.key, col.sortable)}
                    >
                      {col.label}{col.sortable ? sortArrow(col.key) : ''}
                    </Box>
                  ))}
                  <Box component="th" sx={{ px: 0.5, py: 0.5, width: 140, textAlign: 'center', fontSize: '0.65rem', color: 'text.secondary', fontWeight: 700 }}>Actions</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {paginatedRows.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={COLUMNS.length + 1} sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                      {signals.length === 0 ? 'No AdTech signals loaded.' : 'No rows match the filter.'}
                    </Box>
                  </Box>
                ) : (
                  paginatedRows.map((s, idx) => {
                    const focused = !!focusedId && s.registration_id === focusedId;
                    return (
                      <Box
                        component="tr"
                        key={`${s.registration_id}-${s.timestamp ?? ''}-${idx}`}
                        onClick={() => onRowClick?.(s)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: focused ? 'rgba(233,30,99,0.14)' : 'transparent',
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:hover': { bgcolor: focused ? 'rgba(233,30,99,0.2)' : 'action.hover' },
                        }}
                      >
                        {COLUMNS.map((col) => (
                          <Box
                            component="td"
                            key={col.key}
                            sx={{
                              px: 1,
                              py: 0.4,
                              textAlign: col.align,
                              maxWidth: col.width,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {renderCell(s, col)}
                          </Box>
                        ))}
                        <Box component="td" sx={{ px: 0.5, py: 0.4, whiteSpace: 'nowrap', textAlign: 'center' }}>
                          <Tooltip title="Zoom to this observation">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRowClick?.(s); }} sx={{ p: 0.25 }}>
                              <MyLocationIcon sx={{ fontSize: 14, color: '#e91e63' }} />
                            </IconButton>
                          </Tooltip>
                          <AdtechDeviceActions
                            registrationId={s.registration_id}
                            courseId={courseId}
                            onLoadPol={onLoadPol}
                            polLoading={!!polLoading}
                            iconFontSize={14}
                          />
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          {/* Pagination */}
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <TablePagination
              component="div"
              count={pageCount}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Rows:"
              sx={{
                minHeight: 40,
                '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 1 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.7rem' },
              }}
            />
          </Box>

          {/* Resize handles */}
          <Box
            onPointerDown={beginDrag('resize-right')}
            sx={{ position: 'absolute', top: HEADER_HEIGHT, bottom: 8, right: 0, width: 6, cursor: 'ew-resize', zIndex: 3, '&:hover': { bgcolor: 'primary.main', opacity: 0.4 } }}
          />
          <Box
            onPointerDown={beginDrag('resize-bottom')}
            sx={{ position: 'absolute', left: 0, right: 8, bottom: 0, height: 6, cursor: 'ns-resize', zIndex: 3, '&:hover': { bgcolor: 'primary.main', opacity: 0.4 } }}
          />
          <Box
            onPointerDown={beginDrag('resize-corner')}
            sx={{ position: 'absolute', right: 0, bottom: 0, width: 14, height: 14, cursor: 'nwse-resize', zIndex: 4 }}
          />
        </Box>
    </Paper>
  );

  return createPortal(
    <>
      {panel}
      <AdtechInfoDialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        onApplyFlagFilter={(flagName) => {
          setFilter(flagName);
          setPage(0);
          setInfoOpen(false);
        }}
      />
    </>,
    document.body,
  );
};

export default AdtechResultsTable;
